import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../store/auth-context';
import { filterAssignableRoles } from '../../utils/roleUtils';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import {
    getFacilityById,
    getFacilityUsers,
    type FacilityUser,
} from '../../services/api/facilityService';
import {
    assignDeptPermission,
    deleteInvitation,
    deactivateUser,
    getDeparimentInfo,
    removeDeptPermission,
    sendInvitationReminders,
} from '../../services/apiService';
import {
    getAvailableRoles,
    getMyDepartmentUsers,
    updateUserRole,
    type RoleOption,
} from '../../services/api/onboardingService';
import {
    completePolicyDocument,
    createDepartmentPolicy,
    deletePolicy,
    getManagedPolicies,
} from '../../services/api/policyService';
import {
    getFacilityMetrics,
    getMyFacilityMetrics,
    type FacilityMetricsResult,
} from '../../services/api/facilityMetricsService';
import {
    DEFAULT_PERIOD,
    periodToDateRange,
    type PeriodState,
} from './subscription/periodUtils';
import FacilityDialog from './FacilityFormDialog';
import FacilityDetailHeader from './DetailHeader';
import FacilityOverviewSection from './OverviewSection';
import FacilityUsersSection from './UsersSection';
import FacilityPoliciesSection from './FacilityPoliciesSection';
import FacilityScannersSection from './FacilityScannersSection';
import BetaFeaturesSection from './BetaFeaturesSection';
import DepartmentGuestsSection from './DepartmentGuestsSection';
import DeptPermissionTemplatesSection, {
    DEPARTMENT_ROLES_SECTION_ID,
} from './DeptPermissionTemplatesSection';
import {
    policyListItemToParsed,
    type ParsedPolicy,
} from '../department-policies/upload/DocxPolicyUpload';
import { POLICY_SCHEMA_KEY } from '../department-policies/constants';
import FEATURE_KEYS from '../../constants/feature-keys';
import RIGHTS_KEYS from '../../constants/rights-keys';
import InviteSiteAdminDialog from './InviteSiteAdminDialog';
import InviteUsersDialog from './InviteUsersDialog';
import DeactivateFacilityDialog from './DeactivateFacilityDialog';
import type { FacilityPageViewer } from './utils/facilityPageTypes';
import { type UserRow } from './tables/userManagementTypes';
import { deriveFacilityStatus, isFacilityDeactivated, type Facility } from './utils/utils';
import type { EditFacilityUserValues } from './EditUserPermissions';

interface FacilityDetailProps {
    viewer: FacilityPageViewer;
}

export default function FacilityDetail({ viewer }: FacilityDetailProps) {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { t } = useTranslation();
    const authContext = useContext(AuthContext);
    const [facility, setFacility] = useState<Facility | null>(null);
    const [users, setUsers] = useState<FacilityUser[]>([]);
    const [readiness, setReadiness] = useState<FacilityMetricsResult | null>(
        null
    );
    const [selectedPeriod, setSelectedPeriod] =
        useState<PeriodState>(DEFAULT_PERIOD);
    const [roles, setRoles] = useState<RoleOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [inviteUsersOpen, setInviteUsersOpen] = useState(false);
    const [inviteAdminOpen, setInviteAdminOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deactivateOpen, setDeactivateOpen] = useState(false);
    const [dashboardPolicies, setDashboardPolicies] = useState<ParsedPolicy[]>(
        []
    );

    const isNiAdmin = viewer === 'niAdmin';
    const canViewTemplates =
        isNiAdmin ||
        authContext.orRights([
            RIGHTS_KEYS.manageEmployees,
            RIGHTS_KEYS.manageEndUsers,
        ]);
    const canEditTemplates =
        isNiAdmin || authContext.orRights([RIGHTS_KEYS.manageEmployees]);

    const refreshMetrics = useCallback(
        async (facilityId: string, period: PeriodState) => {
            const result = await loadMetrics(
                isNiAdmin,
                facilityId,
                periodToDateRange(period)
            );
            setReadiness(result);
        },
        [isNiAdmin]
    );

    const fetchData = useCallback(async () => {
        setLoading(true);

        try {
            const nextFacility = isNiAdmin
                ? await loadNiAdminFacility(id)
                : await loadSiteAdminFacility();

            setFacility(nextFacility.facility);
            setUsers(nextFacility.users);

            const availableRoles = await getAvailableRoles();
            const rolesArray = Array.isArray(availableRoles)
                ? availableRoles
                : [];
            setRoles(filterAssignableRoles(rolesArray, authContext));
        } catch {
            toast({
                title: t('facilities.detail.loadError'),
                description: t('facilities.detail.loadErrorDesc'),
                variant: 'destructive',
            });
        } finally {
            setLoading(false);
        }
    }, [id, isNiAdmin, toast, authContext, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Refresh only the user list, without the page-level loading state.
    const refreshUsers = useCallback(async () => {
        try {
            if (isNiAdmin) {
                if (!id) return;
                setUsers(await getFacilityUsers(id));
            } else {
                const users = await getMyDepartmentUsers();
                setUsers(Array.isArray(users) ? users : []);
            }
        } catch {
            // keep the current list on failure
        }
    }, [id, isNiAdmin]);

    useEffect(() => {
        if (!facility?._id) {
            setReadiness(null);
            return;
        }

        let cancelled = false;
        const load = async () => {
            const result = await loadMetrics(
                isNiAdmin,
                facility._id,
                periodToDateRange(selectedPeriod)
            );
            if (!cancelled) {
                setReadiness(result);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [
        facility?._id,
        facility?.isTrial,
        facility?.start_date,
        facility?.end_date,
        isNiAdmin,
        selectedPeriod,
    ]);

    useEffect(() => {
        if (isNiAdmin) return;
        let cancelled = false;
        const loadPolicies = async () => {
            try {
                const result = await getManagedPolicies({
                    tab: 'completed',
                    limit: 100,
                });
                if (cancelled) return;
                const existing = result.data.map(policyListItemToParsed);
                setDashboardPolicies((prev) => {
                    const knownIds = new Set(
                        prev.map((p) => p._id).filter(Boolean)
                    );
                    const missing = existing.filter(
                        (p) => !knownIds.has(p._id)
                    );
                    return [...prev, ...missing];
                });
            } catch {
                // silent
            }
        };
        loadPolicies();
        return () => {
            cancelled = true;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePeriodChange = useCallback(
        (period: PeriodState) => {
            setSelectedPeriod(period);
        },
        []
    );

    if (loading) {
        return (
            <div className="container mx-auto max-w-5xl px-4 py-8">
                <p className="text-muted-foreground">{t('facilities.detail.loading')}</p>
            </div>
        );
    }

    if (!facility) {
        return (
            <div className="container mx-auto max-w-5xl px-4 py-8">
                {isNiAdmin && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/admin/facilities')}
                    >
                        <ArrowLeft
                            className="mr-2 h-4 w-4"
                            aria-hidden="true"
                        />
                        {t('facilities.detail.backToFacilities')}
                    </Button>
                )}
                <p className="mt-4 text-muted-foreground">
                    {t('facilities.detail.notFound')}
                </p>
            </div>
        );
    }

    const status = deriveFacilityStatus(facility);
    const isOnboardingStage =
        status === 'onboarding' || status === 'not-started';

    const handleRoleChange = async (userId: string, roleId: string) => {
        const roleName = roles.find((role) => role._id === roleId)?.role;

        setUsers((prev) =>
            prev.map((user) =>
                user._id === userId
                    ? {
                          ...user,
                          role: roleName || user.role,
                          roleId,
                      }
                    : user
            )
        );

        try {
            await updateUserRole(
                userId,
                roleId,
                isNiAdmin ? facility._id : undefined
            );
            if (facility) {
                await refreshMetrics(facility._id, selectedPeriod);
            }
        } catch {
            await fetchData();
        }
    };

    const handleBulkRoleAssign = async (userIds: string[], roleId: string) => {
        const roleName = roles.find((role) => role._id === roleId)?.role;
        const editableUserIds = users
            .filter(
                (user) =>
                    userIds.includes(user._id) && user.role !== 'DeptAdmin'
            )
            .map((user) => user._id);

        setUsers((prev) =>
            prev.map((user) =>
                editableUserIds.includes(user._id)
                    ? {
                          ...user,
                          role: roleName || user.role,
                          roleId,
                      }
                    : user
            )
        );

        try {
            await Promise.all(
                editableUserIds.map((userId) =>
                    updateUserRole(
                        userId,
                        roleId,
                        isNiAdmin ? facility._id : undefined
                    )
                )
            );
            if (facility) {
                await refreshMetrics(facility._id, selectedPeriod);
            }
        } catch {
            await fetchData();
        }
    };

    const handleSendReminders = async (
        invitationRows: UserRow[],
        message: { title: string; message: string }
    ): Promise<boolean> => {
        try {
            await sendInvitationReminders(
                invitationRows.map((user) => ({
                    id: user._id,
                    email: user.email,
                })),
                message.title,
                message.message
            );
            toast({
                title: t(invitationRows.length === 1 ? 'facilities.detail.toasts.reminderSent' : 'facilities.detail.toasts.remindersSent'),
                description:
                    invitationRows.length === 1
                        ? t('facilities.detail.toasts.reminderSentDesc', { email: invitationRows[0].email })
                        : t('facilities.detail.toasts.remindersSentDesc', { count: invitationRows.length }),
            });
            return true;
        } catch {
            toast({
                title: t('facilities.detail.toasts.reminderFailed'),
                description: t('facilities.detail.toasts.reminderFailedDesc'),
                variant: 'destructive',
            });
            return false;
        }
    };

    const handleDeleteInvitation = async (invitationId: string): Promise<boolean> => {
        try {
            const deleted = await deleteInvitation(invitationId);
            if (!deleted) throw new Error();

            toast({ title: t('facilities.detail.toasts.invitationDeleted') });
            await fetchData();
            return true;
        } catch {
            toast({
                title: t('facilities.detail.toasts.deleteFailed'),
                description: t('facilities.detail.toasts.deleteFailedDesc'),
                variant: 'destructive',
            });
            return false;
        }
    };

    const handleRevokeAccess = async (user: UserRow): Promise<boolean> => {
        const revokedAt = new Date();
        revokedAt.setDate(revokedAt.getDate() - 1);

        try {
            await deactivateUser(user._id, revokedAt);
            toast({
                title: t('facilities.detail.toasts.accessRevoked'),
                description: t('facilities.detail.toasts.accessRevokedDesc', { email: user.email }),
            });
            await fetchData();
            return true;
        } catch {
            toast({
                title: t('facilities.detail.toasts.revokeFailed'),
                description: t('facilities.detail.toasts.revokeFailedDesc'),
                variant: 'destructive',
            });
            return false;
        }
    };

    const handleEditUser = async (
        user: UserRow,
        values: EditFacilityUserValues
    ): Promise<boolean> => {
        const roleName = roles.find((role) => role._id === values.roleId)?.role;
        const nextExpiryDate = new Date(values.expiryDate);
        const currentPermissions = user.deptPermissions ?? [];
        const permissionsToAdd = values.permissions.filter(
            (permission) => !currentPermissions.includes(permission)
        );
        const permissionsToRemove = currentPermissions.filter(
            (permission) => !values.permissions.includes(permission)
        );

        setUsers((prev) =>
            prev.map((item) =>
                item._id === user._id
                    ? {
                          ...item,
                          role: roleName || item.role,
                          expiryDate: nextExpiryDate.toISOString(),
                          deptPermissions: values.permissions,
                      }
                    : item
            )
        );

        try {
            await Promise.all([
                updateUserRole(
                    user._id,
                    values.roleId,
                    isNiAdmin ? facility._id : undefined
                ),
                deactivateUser(user._id, nextExpiryDate),
            ]);
            await Promise.all([
                ...permissionsToAdd.map((permission) =>
                    assignDeptPermission(user._id, permission)
                ),
                ...permissionsToRemove.map((permission) =>
                    removeDeptPermission(user._id, permission)
                ),
            ]);
            toast({
                title: t('facilities.detail.toasts.userUpdated'),
                description: t('facilities.detail.toasts.userUpdatedDesc', { email: user.email }),
            });
            await fetchData();
            return true;
        } catch {
            toast({
                title: t('facilities.detail.toasts.updateFailed'),
                description: t('facilities.detail.toasts.updateFailedDesc'),
                variant: 'destructive',
            });
            await fetchData();
            return false;
        }
    };

    const handleAddPolicy = async (policy: ParsedPolicy): Promise<void> => {
        try {
            const created = await createDepartmentPolicy({
                schemaKey: POLICY_SCHEMA_KEY,
                effectiveDate: new Date(policy.effectiveDate).toISOString(),
                expiryDate: new Date(policy.expiryDate).toISOString(),
                tags: policy.tags,
            });
            await completePolicyDocument(
                created._id,
                { policyData: policy.html },
                policy.title,
                t('facilities.detail.importedFromDocx')
            );
            setDashboardPolicies((prev) => [
                ...prev,
                { ...policy, _id: created._id },
            ]);
            await refreshMetrics(facility._id, selectedPeriod);
        } catch {
            toast({
                title: t('facilities.detail.toasts.updateFailed'),
                description: t('facilities.detail.toasts.updateFailedDesc'),
                variant: 'destructive',
            });
        }
    };

    const handleUpdatePolicy = (updated: ParsedPolicy) => {
        setDashboardPolicies((prev) =>
            prev.map((p) => (p._id === updated._id ? updated : p))
        );
    };

    const handleRemovePolicy = async (
        policy: ParsedPolicy
    ): Promise<void> => {
        try {
            if (policy._id) {
                await deletePolicy(policy._id);
            }
            setDashboardPolicies((prev) => prev.filter((p) => p !== policy));
            await refreshMetrics(facility._id, selectedPeriod);
        } catch {
            toast({
                title: t('facilities.detail.toasts.updateFailed'),
                description: t('facilities.detail.toasts.updateFailedDesc'),
                variant: 'destructive',
            });
        }
    };

    return (
        <>
            <div className="container mx-auto max-w-5xl px-4 py-8">
                <FacilityDetailHeader
                    facility={facility}
                    viewer={viewer}
                    onBack={() => navigate('/admin/facilities')}
                    onEdit={() => setEditOpen(true)}
                    onInviteAdmin={() => setInviteAdminOpen(true)}
                    onDeactivate={
                        isNiAdmin && !isFacilityDeactivated(facility)
                            ? () => setDeactivateOpen(true)
                            : undefined
                    }
                />

                <FacilityOverviewSection
                    facility={facility}
                    metrics={readiness}
                    status={status}
                    viewer={viewer}
                    selectedPeriod={selectedPeriod}
                    onPeriodChange={handlePeriodChange}
                />

                <FacilityUsersSection
                    users={users}
                    roles={roles}
                    departmentId={isNiAdmin ? facility._id : undefined}
                    viewer={viewer}
                    isOnboardingStage={isOnboardingStage}
                    isDeactivated={
                        !!facility.end_date &&
                        new Date(facility.end_date) < new Date()
                    }
                    trialStartDate={facility.start_date}
                    isTrial={facility.isTrial}
                    onInviteUsers={() => setInviteUsersOpen(true)}
                    onRoleChange={handleRoleChange}
                    onBulkRoleAssign={handleBulkRoleAssign}
                    onSendReminders={handleSendReminders}
                    onDeleteInvitation={handleDeleteInvitation}
                    onEditUser={handleEditUser}
                    onRevokeAccess={handleRevokeAccess}
                    onGoToRoles={
                        canViewTemplates
                            ? () =>
                                  document
                                      .getElementById(
                                          DEPARTMENT_ROLES_SECTION_ID
                                      )
                                      ?.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'start',
                                      })
                            : undefined
                    }
                />

                {canViewTemplates && (
                    <DeptPermissionTemplatesSection
                        departmentId={facility._id}
                        viewer={viewer}
                        users={users}
                        canEditTemplates={canEditTemplates}
                        onUsersChanged={refreshUsers}
                    />
                )}

                {isOnboardingStage && !isNiAdmin && (
                    <FacilityScannersSection onChange={fetchData} />
                )}

                {isOnboardingStage && !isNiAdmin && (
                    <FacilityPoliciesSection
                        policies={dashboardPolicies}
                        onAdd={handleAddPolicy}
                        onRemove={handleRemovePolicy}
                        onUpdate={handleUpdatePolicy}
                    />
                )}

                {!isNiAdmin && !isOnboardingStage && facility.featureAccess?.includes(FEATURE_KEYS.guestAccess) && (
                    <DepartmentGuestsSection />
                )}

                {!isNiAdmin &&
                    !isOnboardingStage &&
                    authContext.orRights([
                        RIGHTS_KEYS.manageEmployees,
                        RIGHTS_KEYS.manageEndUsers,
                    ]) && <BetaFeaturesSection />}

                <FacilityDialog
                    open={editOpen}
                    onOpenChange={setEditOpen}
                    facility={facility}
                    onSaved={fetchData}
                    viewMode={isNiAdmin ? 'admin' : 'siteAdmin'}
                />
                <InviteSiteAdminDialog
                    open={inviteAdminOpen}
                    onOpenChange={setInviteAdminOpen}
                    departmentId={facility._id}
                    facilityName={facility.name}
                    onInvited={fetchData}
                />
                <InviteUsersDialog
                    open={inviteUsersOpen}
                    onOpenChange={setInviteUsersOpen}
                    facilityName={facility.name}
                    departmentId={isNiAdmin ? facility._id : undefined}
                    isOnboarding={isOnboardingStage}
                    onInvited={fetchData}
                    licenses={facility.licenses}
                    usedSlots={
                        (facility.active_licenses ?? 0) +
                        (facility.pendingInviteCount ?? 0)
                    }
                />
                <DeactivateFacilityDialog
                    facility={deactivateOpen ? facility : null}
                    onOpenChange={setDeactivateOpen}
                    onDeactivated={() => navigate('/admin/facilities')}
                />
            </div>
        </>
    );
}

async function loadMetrics(
    isNiAdmin: boolean,
    facilityId: string,
    period: { start: Date; end: Date }
): Promise<FacilityMetricsResult | null> {
    const params = { periodStart: period.start, periodEnd: period.end };
    try {
        return isNiAdmin
            ? await getFacilityMetrics(facilityId, params)
            : await getMyFacilityMetrics(params);
    } catch {
        return null;
    }
}

async function loadNiAdminFacility(
    facilityId?: string
): Promise<{ facility: Facility | null; users: FacilityUser[] }> {
    if (!facilityId) {
        return { facility: null, users: [] };
    }

    const facility = await getFacilityById(facilityId);
    const users = facility ? await getFacilityUsers(facility._id) : [];

    return { facility, users };
}

async function loadSiteAdminFacility(): Promise<{
    facility: Facility | null;
    users: FacilityUser[];
}> {
    const departmentInfo = await getDeparimentInfo();
    const users = await getMyDepartmentUsers();

    return {
        facility: departmentInfo as Facility,
        users: Array.isArray(users) ? users : [],
    };
}
